import { useEffect, useId, useRef, useState } from 'react'

import type { Clause } from '@/api/actions'
import { Textarea } from '@/components/ui/input'

import { describeClause } from '../describe'
import { useEditor } from './context'
import { asList, asObj, asStrings, blankClause, clone, insertAt, isObj, moveItem, pretty, removeAt, setOrDelete, splitStateRef, type Obj } from './doc'
import { ComboInput, EditField, ItemCard, ListControls, nameNormalize, NumberInput, SELECT_CLASS, SelectInput, TagInput, ValueInput } from './fields'
import { useListKeys } from './listKeys'
import { RoleSpecFields } from './RoleSpecFields'

/**
 * "What must be true first" (§11.3): a list of checks, each read back in plain words as you build
 * it. Kinds: a participant must be something, two participants relate (near, holding, taller…), the
 * place has something, a state is / is not / is one of, at least one of several checks, or NOT a
 * check. `any` and `not` nest.
 */

export const CLAUSE_LABEL: Record<string, string> = {
  role: 'A participant must be…',
  relation: 'Two of them are…',
  stage: 'The place has…',
  state: 'Something is…',
  any: 'At least one of these',
  not: 'This must NOT be true',
}

const RELATION_LABEL: Record<string, string> = {
  near: 'is near',
  far: 'is far from',
  holding: 'is holding',
  held_by: 'is held by',
  on: 'is on',
  inside: 'is inside',
  behind: 'is behind',
  taller_than: 'is taller than',
  shorter_than: 'is shorter than',
  can_reach: 'can reach',
  same_region: 'is in the same place as',
  in_region: 'is in',
}

const PLACE_KINDS = ['water', 'ground', 'sky', 'wall', 'ceiling', 'foreground', 'scenery']
const MAX_DEPTH = 3

function KindPicker({ kinds, onPick, label }: { kinds: string[]; onPick(kind: string): void; label: string }) {
  const id = useId()
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label htmlFor={id} className="text-sm text-ink-2">
        {label}
      </label>
      <select
        id={id}
        className={`${SELECT_CLASS} w-auto`}
        value=""
        onChange={(e) => {
          if (e.target.value) onPick(e.target.value)
        }}
      >
        <option value="">Choose a kind of check…</option>
        {kinds.map((k) => (
          <option key={k} value={k}>
            {CLAUSE_LABEL[k] ?? k}
          </option>
        ))}
      </select>
    </div>
  )
}

export function ClauseList({ path, clauses, onChange, depth = 0, addLabel = 'Add a check' }: { path: string; clauses: unknown[]; onChange(next: unknown[]): void; depth?: number; addLabel?: string }) {
  const { meta, roles } = useEditor()
  const kinds = meta.clause_kinds.filter((k) => depth < MAX_DEPTH || (k !== 'any' && k !== 'not'))
  const [keys, keyOps] = useListKeys(clauses.length)
  return (
    <div className="flex flex-col gap-3">
      {clauses.length === 0 && depth === 0 ? <p className="text-sm text-ink-2">Nothing special: it can always happen when its participants are there.</p> : null}
      {clauses.map((c, i) => (
        <ClauseCard
          key={keys[i]}
          path={`${path}.${i}`}
          clause={asObj(c)}
          depth={depth}
          name={`check ${i + 1}`}
          index={i}
          count={clauses.length}
          onChange={(next) => onChange(clauses.map((x, j) => (j === i ? next : x)))}
          onMove={(by) => {
            keyOps.move(i, by)
            onChange(moveItem(clauses, i, by))
          }}
          onDuplicate={() => {
            keyOps.insert(i + 1)
            onChange(insertAt(clauses, i + 1, clone(c)))
          }}
          onRemove={() => {
            keyOps.remove(i)
            onChange(removeAt(clauses, i))
          }}
        />
      ))}
      <KindPicker kinds={kinds} label={addLabel} onPick={(k) => onChange([...clauses, blankClause(k, roles)])} />
    </div>
  )
}

function ClauseCard({
  path,
  clause,
  depth,
  name,
  index,
  count,
  onChange,
  onMove,
  onDuplicate,
  onRemove,
}: {
  path: string
  clause: Obj
  depth: number
  name: string
  index: number
  count: number
  onChange(next: Obj): void
  onMove(by: -1 | 1): void
  onDuplicate(): void
  onRemove(): void
}) {
  return (
    <ItemCard
      path={path}
      testId="clause"
      title={
        <span className="flex flex-col gap-0.5">
          <span>{CLAUSE_LABEL[String(clause.k)] ?? String(clause.k ?? 'Check')}</span>
          <span className="text-xs font-normal text-ink-2" data-testid="clause-reading">
            {describeClause(clause as Clause)}
          </span>
        </span>
      }
      controls={<ListControls name={name} index={index} count={count} onMove={onMove} onDuplicate={onDuplicate} onRemove={onRemove} />}
    >
      <ClauseBody path={path} clause={clause} depth={depth} onChange={onChange} />
    </ItemCard>
  )
}

function ClauseBody({ path, clause, depth, onChange }: { path: string; clause: Obj; depth: number; onChange(next: Obj): void }) {
  const { meta, roles } = useEditor()
  const roleOpts = roles.map((r) => ({ value: `$${r}`, label: r.replace(/_/g, ' ') }))
  switch (clause.k) {
    case 'role': {
      const role = typeof clause.role === 'string' ? clause.role : ''
      const { k, role: _r, ...spec } = clause
      return (
        <div className="flex flex-col gap-3">
          <EditField path={`${path}.role`} label="Who">
            {(ctrl) => <SelectInput ctrl={ctrl} value={role || undefined} options={roles.map((r) => ({ value: r, label: r.replace(/_/g, ' ') }))} onChange={(v) => onChange({ ...clause, role: v ?? '' })} />}
          </EditField>
          <RoleSpecFields path={path} spec={spec} who={role ? `the ${role.replace(/_/g, ' ')}` : 'it'} onChange={(next) => onChange({ k, role: clause.role, ...next })} />
        </div>
      )
    }
    case 'relation':
      return (
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_7rem]">
          <EditField path={`${path}.a`} label="This one">
            {(ctrl) => <SelectInput ctrl={ctrl} value={typeof clause.a === 'string' ? clause.a : undefined} options={roleOpts} onChange={(v) => onChange({ ...clause, a: v ?? '' })} />}
          </EditField>
          <EditField path={`${path}.rel`} label="How">
            {(ctrl) => (
              <SelectInput ctrl={ctrl} value={typeof clause.rel === 'string' ? clause.rel : undefined} options={meta.relations.map((r) => ({ value: r, label: RELATION_LABEL[r] ?? r.replace(/_/g, ' ') }))} onChange={(v) => onChange({ ...clause, rel: v ?? 'near' })} />
            )}
          </EditField>
          <EditField path={`${path}.b`} label="That one" hint="A participant, or kind:water for any water.">
            {(ctrl) => <ComboInput ctrl={ctrl} value={clause.b} suggestions={[...roleOpts, ...PLACE_KINDS.map((p) => ({ value: `kind:${p}`, label: `any ${p}` }))]} onChange={(v) => onChange({ ...clause, b: v ?? '' })} />}
          </EditField>
          <EditField path={`${path}.ratio`} label="By (×)" hint="Optional.">
            {(ctrl) => <NumberInput ctrl={ctrl} value={clause.ratio} min={0} step={0.05} onChange={(v) => onChange(setOrDelete(clause, ['ratio'], v))} />}
          </EditField>
        </div>
      )
    case 'stage':
      return (
        <div className="grid gap-3 md:grid-cols-2">
          <EditField path={`${path}.has`} label="The place has" hint="water, ground, sky — or an ability like swimmable.">
            {(ctrl) => <TagInput ctrl={ctrl} values={asStrings(clause.has)} onChange={(v) => onChange({ ...clause, has: v })} suggestions={PLACE_KINDS} placeholder="water…" />}
          </EditField>
          <EditField path={`${path}.world_any`} label="Only in these worlds" hint="Leave empty for any world.">
            {(ctrl) => <TagInput ctrl={ctrl} values={asStrings(clause.world_any)} onChange={(v) => onChange(setOrDelete(clause, ['world_any'], v))} placeholder="forest, beach…" />}
          </EditField>
        </div>
      )
    case 'state':
      return <StateClauseFields path={path} clause={clause} onChange={onChange} />
    case 'any':
      return <ClauseList path={`${path}.any`} clauses={asList(clause.any)} depth={depth + 1} addLabel="Add another way" onChange={(next) => onChange({ ...clause, any: next })} />
    case 'not': {
      const inner = asObj(clause.not)
      return (
        <div className="flex flex-col gap-3 border-l-2 border-line pl-3">
          <KindPicker kinds={meta.clause_kinds.filter((k) => depth + 1 < MAX_DEPTH || (k !== 'any' && k !== 'not'))} label="Change what must not be true" onPick={(k) => onChange({ ...clause, not: blankClause(k, roles) })} />
          <p className="text-xs font-medium uppercase tracking-wide text-ink-3">{CLAUSE_LABEL[String(inner.k)] ?? 'Check'}</p>
          <ClauseBody path={`${path}.not`} clause={inner} depth={depth + 1} onChange={(next) => onChange({ ...clause, not: next })} />
        </div>
      )
    }
    default:
      return <RawJsonField path={path} value={clause} onChange={(v) => (isObj(v) ? onChange(v) : undefined)} label="This check, as JSON" />
  }
}

const OPS = [
  { value: 'eq', label: 'is' },
  { value: 'ne', label: 'is not' },
  { value: 'in', label: 'is one of' },
]

function StateClauseFields({ path, clause, onChange }: { path: string; clause: Obj; onChange(next: Obj): void }) {
  const { roles, meta, lists } = useEditor()
  const [role, key] = splitStateRef(clause.ref)
  const op = 'in' in clause ? 'in' : 'ne' in clause ? 'ne' : 'eq'
  const value = clause[op]
  const setRef = (r: string, k: string): void => onChange({ ...clause, ref: `$${r}.${k}` })
  const setOp = (next: string): void => {
    const { eq: _e, ne: _n, in: _i, ...rest } = clause
    const v = next === 'in' ? (Array.isArray(value) ? value : value === undefined ? [] : [value]) : Array.isArray(value) ? value[0] : value
    onChange({ ...rest, [next]: v ?? null })
  }
  return (
    <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_8rem_minmax(0,1.2fr)]">
      <EditField path={`${path}.ref`} label="Whose" deep={false}>
        {(ctrl) => <SelectInput ctrl={ctrl} value={role || undefined} options={roles.map((r) => ({ value: r, label: r.replace(/_/g, ' ') }))} onChange={(v) => setRef(v ?? '', key || 'mood')} />}
      </EditField>
      <EditField path={`${path}.ref.key`} label="What">
        {(ctrl) => <ComboInput ctrl={ctrl} value={key} suggestions={meta.state_keys} listId={lists?.stateKeys} normalize={nameNormalize} onChange={(k) => setRef(role || roles[0] || 'actor', k ?? '')} />}
      </EditField>
      <EditField path={`${path}.op`} label="Is">
        {(ctrl) => <SelectInput ctrl={ctrl} value={op} options={OPS} onChange={(v) => setOp(v ?? 'eq')} />}
      </EditField>
      {op === 'in' ? (
        <EditField path={`${path}.in`} label="One of">
          {(ctrl) => <TagInput ctrl={ctrl} values={asList(value).map(String)} onChange={(v) => onChange({ ...clause, in: v })} normalize={(s) => s.trim()} placeholder="happy, sad…" />}
        </EditField>
      ) : (
        <EditField path={`${path}.${op}`} label="Value">
          {(ctrl) => <ValueInput ctrl={ctrl} value={value} stateKey={key} onChange={(v) => onChange({ ...clause, [op]: v })} />}
        </EditField>
      )}
    </div>
  )
}

/** A JSON box for a part the form has no fields for (a future clause kind, a new track field). */
export function RawJsonField({ path, value, onChange, label }: { path: string; value: unknown; onChange(v: unknown): void; label: string }) {
  const [text, setText] = useState(() => pretty(value))
  const [error, setError] = useState<string | null>(null)
  // Follow outside changes (the JSON tab, "Undo my changes") without fighting what is being typed.
  const sent = useRef(pretty(value))
  const now = pretty(value)
  useEffect(() => {
    if (now !== sent.current) {
      sent.current = now
      setText(now)
      setError(null)
    }
  }, [now])
  return (
    <EditField path={path} label={label} deep={false} hint={error ?? 'Changes are used as soon as they are valid JSON.'}>
      {(ctrl) => (
        <Textarea
          id={ctrl.id}
          rows={4}
          value={text}
          spellCheck={false}
          className="font-mono text-xs"
          aria-invalid={error !== null || ctrl.invalid || undefined}
          aria-describedby={ctrl.describedBy}
          onChange={(e) => {
            setText(e.target.value)
            try {
              const v: unknown = JSON.parse(e.target.value)
              sent.current = pretty(v)
              onChange(v)
              setError(null)
            } catch {
              setError('Not valid JSON yet.')
            }
          }}
        />
      )}
    </EditField>
  )
}
